import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { inventory } from '../services/api';
import { Search, Plus, Package, Trash2, PlusCircle, X, Check, ChevronDown, ChevronRight, Tag, AlertTriangle } from 'lucide-react';

const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '28', '30', '32', '34', '36', '38', '40', 'FREE'];
const COLORS = ['Black', 'White', 'Grey', 'Navy', 'Red', 'Blue', 'Green', 'Yellow', 'Pink', 'Brown', 'Beige', 'Olive', 'Maroon'];

interface SizeEntry { size: string; qty: number; cost: number; enabled: boolean; }

interface GroupedItem {
  key: string;
  article_type: string;
  color: string;
  items: any[];
  totalQty: number;
  totalValue: number;
  avgCost: number;
  hasLowStock: boolean;
}

const Inventory: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showPurchase, setShowPurchase] = useState<number | null>(null);
  const [editQty, setEditQty] = useState<{ id: number; qty: number } | null>(null);
  const [filter, setFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Add form state
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [articleType, setArticleType] = useState('');
  const [selectedColor, setSelectedColor] = useState('Black');
  const [sizeEntries, setSizeEntries] = useState<SizeEntry[]>(
    DEFAULT_SIZES.map(s => ({ size: s, qty: 0, cost: 0, enabled: false }))
  );
  const [customSize, setCustomSize] = useState('');
  const [notifyBelow, setNotifyBelow] = useState(10);
  const [globalCost, setGlobalCost] = useState('');

  // Category form
  const [newCatName, setNewCatName] = useState('');
  const [newCatSizes, setNewCatSizes] = useState('');

  // Purchase form
  const [purchaseForm, setPurchaseForm] = useState({ quantity: 0, total_cost: 0, notes: '' });

  const load = () => {
    inventory.getAll().then(r => setItems(r.data)).catch(() => {});
    inventory.getCategories().then(r => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleCategoryChange = (catId: number) => {
    setSelectedCategory(catId);
    const cat = categories.find(c => c.id === catId);
    if (cat) {
      setArticleType(cat.name);
      const catSizes = cat.sizes?.length > 0 ? cat.sizes : DEFAULT_SIZES;
      setSizeEntries(catSizes.map((s: string) => ({ size: s, qty: 0, cost: 0, enabled: false })));
    }
  };

  const toggleSize = (index: number) => {
    const updated = [...sizeEntries];
    updated[index].enabled = !updated[index].enabled;
    if (!updated[index].enabled) { updated[index].qty = 0; updated[index].cost = 0; }
    setSizeEntries(updated);
  };

  const updateSizeEntry = (index: number, field: 'qty' | 'cost', value: number) => {
    const updated = [...sizeEntries];
    updated[index][field] = value;
    if (!updated[index].enabled) updated[index].enabled = true;
    setSizeEntries(updated);
  };

  const applyGlobalCost = () => {
    const cost = parseFloat(globalCost);
    if (isNaN(cost)) return;
    setSizeEntries(sizeEntries.map(e => e.enabled ? { ...e, cost } : e));
  };

  const addCustomSize = () => {
    if (!customSize.trim()) return;
    setSizeEntries([...sizeEntries, { size: customSize.trim().toUpperCase(), qty: 0, cost: 0, enabled: false }]);
    setCustomSize('');
  };

  const resetForm = () => {
    setSelectedCategory(0);
    setArticleType('');
    setSelectedColor('Black');
    setSizeEntries(DEFAULT_SIZES.map(s => ({ size: s, qty: 0, cost: 0, enabled: false })));
    setGlobalCost('');
    setNotifyBelow(10);
  };

  const handleBulkAdd = async () => {
    const entries = sizeEntries.filter(s => s.enabled && s.qty > 0);
    if (!articleType || entries.length === 0) return;
    const items = entries.map(e => ({
      article_type: articleType,
      color: selectedColor,
      size: e.size,
      quantity: e.qty,
      cost_price: e.cost,
      notify_below: notifyBelow,
    }));
    await inventory.createBulk(items);
    setShowAdd(false);
    resetForm();
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this item?')) return;
    await inventory.delete(id);
    load();
  };

  const handleAddCategory = async () => {
    if (!newCatName) return;
    const sizes = newCatSizes ? newCatSizes.split(',').map(s => s.trim()).filter(Boolean) : [];
    await inventory.createCategory({ name: newCatName, sizes });
    setNewCatName('');
    setNewCatSizes('');
    load();
  };

  const handlePurchase = async (id: number) => {
    await inventory.addPurchase({ inventory_id: id, ...purchaseForm });
    setShowPurchase(null);
    setPurchaseForm({ quantity: 0, total_cost: 0, notes: '' });
    load();
  };

  const handleQtyUpdate = async (id: number, qty: number) => {
    await inventory.update(id, { quantity: qty });
    setEditQty(null);
    load();
  };

  // ── Grouping logic ──
  const filtered = items.filter(i => {
    const matchesText = `${i.article_type} ${i.color} ${i.size}`.toLowerCase().includes(filter.toLowerCase());
    const matchesCat = !catFilter || i.article_type === catFilter;
    return matchesText && matchesCat;
  });

  const grouped: GroupedItem[] = useMemo(() => {
    const map = new Map<string, any[]>();
    filtered.forEach(item => {
      const key = `${item.color} ${item.article_type}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });

    return Array.from(map.entries()).map(([key, groupItems]) => {
      // Sort sizes
      groupItems.sort((a, b) => {
        const ai = SIZE_ORDER.indexOf(a.size.toUpperCase());
        const bi = SIZE_ORDER.indexOf(b.size.toUpperCase());
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
      const totalQty = groupItems.reduce((s, i) => s + i.quantity, 0);
      const totalValue = groupItems.reduce((s, i) => s + i.quantity * i.cost_price, 0);
      const avgCost = groupItems.length > 0 ? groupItems[0].cost_price : 0;
      const hasLowStock = groupItems.some(i => i.quantity <= i.notify_below);
      return { key, article_type: groupItems[0].article_type, color: groupItems[0].color, items: groupItems, totalQty, totalValue, avgCost, hasLowStock };
    }).sort((a, b) => a.key.localeCompare(b.key));
  }, [filtered]);

  const totalValue = items.reduce((sum, i) => sum + i.quantity * i.cost_price, 0);
  const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const activeEntries = sizeEntries.filter(s => s.enabled && s.qty > 0);
  const formTotalQty = activeEntries.reduce((s, e) => s + e.qty, 0);
  const formTotalCost = activeEntries.reduce((s, e) => s + e.qty * e.cost, 0);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px', background: 'var(--bg-input)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: 0.8, display: 'block', marginBottom: 6, fontWeight: 600,
  };

  return (
    <Layout title="Inventory" subtitle={`${totalQty} pieces · ₹${totalValue.toLocaleString('en-IN')} value`}>
      {/* Toolbar */}
      <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', width: 240 }}>
            <Search size={14} color="var(--text-muted)" />
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search articles..."
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%' }} />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...inputStyle, width: 180 }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowCategories(true)} style={{
            padding: '9px 16px', background: 'var(--accent-purple-soft)',
            border: '1px solid rgba(157,122,255,0.15)', borderRadius: 'var(--radius-sm)',
            color: 'var(--accent-purple)', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}><Tag size={14} /> Categories</button>
          <button onClick={() => { resetForm(); setShowAdd(true); }} style={{
            padding: '9px 18px', background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))',
            border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
          }}><Plus size={15} /> Add Stock</button>
        </div>
      </div>

      {/* Grouped Cards */}
      <div className="animate-in-1" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {grouped.map(group => {
          const isOpen = expanded.has(group.key);
          return (
            <div key={group.key} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', overflow: 'hidden',
            }}>
              {/* Group Header — clickable */}
              <div
                onClick={() => toggleExpand(group.key)}
                style={{
                  padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', transition: 'var(--transition)',
                  background: isOpen ? 'var(--bg-elevated)' : 'transparent',
                }}
                onMouseEnter={e => { if (!isOpen) (e.currentTarget as HTMLElement).style.background = 'var(--bg-card-hover)'; }}
                onMouseLeave={e => { if (!isOpen) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {isOpen ? <ChevronDown size={16} color="var(--accent-blue)" /> : <ChevronRight size={16} color="var(--text-muted)" />}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {group.color} {group.article_type}
                      {group.hasLowStock && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                          background: 'var(--accent-red-soft)', color: 'var(--accent-red)',
                        }}><AlertTriangle size={9} /> Low</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                      {group.items.length} size{group.items.length !== 1 ? 's' : ''} · ₹{group.avgCost}/pc
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  {/* Size pills preview when collapsed */}
                  {!isOpen && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {group.items.map(item => (
                        <span key={item.id} style={{
                          fontSize: 10.5, padding: '2px 8px', borderRadius: 6, fontWeight: 600,
                          background: item.quantity <= item.notify_below ? 'var(--accent-red-soft)' : 'var(--accent-blue-soft)',
                          color: item.quantity <= item.notify_below ? 'var(--accent-red)' : 'var(--accent-blue)',
                        }}>{item.size}: {item.quantity}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ textAlign: 'right', minWidth: 70 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.3 }}>{group.totalQty}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>pieces</div>
                  </div>
                  <div style={{ textAlign: 'right', minWidth: 80 }} className="hide-mobile">
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-green)' }}>₹{group.totalValue.toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>value</div>
                  </div>
                </div>
              </div>

              {/* Expanded Size Rows */}
              {isOpen && (
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  {group.items.map(item => {
                    const isEditing = editQty?.id === item.id;
                    const isLow = item.quantity <= item.notify_below;
                    return (
                      <div key={item.id} style={{
                        padding: '10px 20px 10px 52px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        borderBottom: '1px solid var(--border)',
                        background: isLow ? 'rgba(239,90,111,0.03)' : 'transparent',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
                          <span style={{
                            width: 42, textAlign: 'center', padding: '4px 0', borderRadius: 6,
                            fontSize: 12, fontWeight: 700,
                            background: isLow ? 'var(--accent-red-soft)' : 'var(--accent-blue-soft)',
                            color: isLow ? 'var(--accent-red)' : 'var(--accent-blue)',
                          }}>{item.size}</span>

                          {/* Stock */}
                          <div style={{ minWidth: 70 }}>
                            {isEditing && editQty ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input type="number" value={editQty.qty} onChange={e => setEditQty({ id: item.id, qty: Number(e.target.value) })}
                                  style={{ width: 60, padding: '4px 6px', background: 'var(--bg-input)', border: '1px solid var(--accent-blue)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, outline: 'none', textAlign: 'center' }} />
                                <button onClick={() => handleQtyUpdate(item.id, editQty.qty)} style={{ background: 'var(--accent-green-soft)', border: 'none', borderRadius: 4, padding: '3px 5px', cursor: 'pointer', display: 'flex' }}><Check size={12} color="var(--accent-green)" /></button>
                                <button onClick={() => setEditQty(null)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 4, padding: '3px 5px', cursor: 'pointer', display: 'flex' }}><X size={12} color="var(--text-muted)" /></button>
                              </div>
                            ) : (
                              <span onClick={() => setEditQty({ id: item.id, qty: item.quantity })} title="Click to edit" style={{
                               fontSize: 14, fontWeight: 700, cursor: 'pointer',
                               color: isLow ? 'var(--accent-red)' : 'var(--text-primary)',
                               padding: '2px 8px', borderRadius: 6,
                               border: '1px dashed transparent',
                               transition: 'all 0.15s',
                              }}
                               onMouseEnter={e => { e.currentTarget.style.border = '1px dashed var(--accent-blue)'; e.currentTarget.style.background = 'var(--accent-blue-soft)'; }}
                               onMouseLeave={e => { e.currentTarget.style.border = '1px dashed transparent'; e.currentTarget.style.background = 'transparent'; }}
                              >
                               {item.quantity} {isLow && <AlertTriangle size={11} style={{ verticalAlign: 'text-bottom' }} />}
                            </span>
                            )}
                          </div>

                          {/* Cost */}
                          <span style={{ fontSize: 12, color: 'var(--accent-amber)', fontWeight: 500 }}>₹{item.cost_price}</span>

                          {/* Value */}
                          <span style={{ fontSize: 12, color: 'var(--accent-green)', fontWeight: 500 }} className="hide-mobile">
                            ₹{(item.quantity * item.cost_price).toLocaleString('en-IN')}
                          </span>
                        </div>

                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => { setPurchaseForm({ quantity: 0, total_cost: 0, notes: '' }); setShowPurchase(item.id); }} style={{
                            background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.15)',
                            borderRadius: 5, padding: '4px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                            color: 'var(--accent-green)', fontSize: 11, fontWeight: 600,
                          }}><PlusCircle size={11} /> Stock</button>
                          <button onClick={() => handleDelete(item.id)} style={{
                            background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)',
                            borderRadius: 5, padding: '4px 7px', cursor: 'pointer', display: 'flex',
                          }}><Trash2 size={12} color="var(--accent-red)" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {grouped.length === 0 && (
        <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-muted)', fontSize: 13 }}>No items found. Add your first stock!</div>
      )}

      {/* ═══ ADD STOCK MODAL ═══ */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setShowAdd(false); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 0, width: 560, maxWidth: '95vw', boxShadow: 'var(--shadow-elevated)', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 2, borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Add New Stock</div>
                <button onClick={() => setShowAdd(false)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
              </div>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Category</label>
                <select value={selectedCategory} onChange={e => handleCategoryChange(Number(e.target.value))} style={inputStyle}>
                  <option value={0}>Select category...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {categories.length === 0 && <div style={{ fontSize: 12, color: 'var(--accent-amber)', marginTop: 6 }}>No categories yet. Click "Categories" to add one.</div>}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Article Type</label>
                <input value={articleType} onChange={e => setArticleType(e.target.value)} style={inputStyle} placeholder="e.g. Oversized T-Shirt" />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Color</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setSelectedColor(c)} style={{
                      padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      background: selectedColor === c ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)',
                      border: `1px solid ${selectedColor === c ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                      color: selectedColor === c ? 'var(--accent-blue)' : 'var(--text-secondary)',
                    }}>{c}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Sizes — Qty & Cost per piece</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" value={globalCost} onChange={e => setGlobalCost(e.target.value)} placeholder="₹ cost" style={{ width: 80, padding: '5px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                    <button onClick={applyGlobalCost} style={{ padding: '5px 10px', background: 'var(--accent-blue-soft)', border: 'none', borderRadius: 6, color: 'var(--accent-blue)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply to all</button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {sizeEntries.map((entry, i) => (
                    <div key={entry.size} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                      background: entry.enabled ? 'var(--bg-elevated)' : 'transparent',
                      border: `1px solid ${entry.enabled ? 'var(--border-light)' : 'var(--border)'}`,
                    }}>
                      <button onClick={() => toggleSize(i)} style={{
                        width: 50, padding: '5px 0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: entry.enabled ? 'var(--accent-blue)' : 'var(--bg-input)',
                        border: 'none', color: entry.enabled ? '#fff' : 'var(--text-muted)', textAlign: 'center',
                      }}>{entry.size}</button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 10.5, color: 'var(--text-muted)', width: 25 }}>Qty</span>
                        <input type="number" min={0} value={entry.qty || ''} onChange={e => updateSizeEntry(i, 'qty', Number(e.target.value))}
                          placeholder="0" style={{ width: 65, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none', textAlign: 'center' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 10.5, color: 'var(--text-muted)', width: 30 }}>₹</span>
                        <input type="number" min={0} step={0.01} value={entry.cost || ''} onChange={e => updateSizeEntry(i, 'cost', Number(e.target.value))}
                          placeholder="0" style={{ width: 80, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--accent-amber)', fontSize: 13, outline: 'none', textAlign: 'right' }} />
                      </div>
                      <div style={{ flex: 1, textAlign: 'right', fontSize: 12, color: entry.enabled && entry.qty > 0 ? 'var(--accent-green)' : 'var(--text-muted)', fontWeight: 600 }}>
                        {entry.qty > 0 && entry.cost > 0 ? `₹${(entry.qty * entry.cost).toLocaleString('en-IN')}` : '—'}
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <input value={customSize} onChange={e => setCustomSize(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addCustomSize(); }}
                      placeholder="Custom size (e.g. 28, 30, Free)" style={{ flex: 1, padding: '8px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                    <button onClick={addCustomSize} style={{ padding: '8px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>+ Add Size</button>
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Low Stock Alert (min qty)</label>
                <input type="number" value={notifyBelow} onChange={e => setNotifyBelow(Number(e.target.value))} style={{ ...inputStyle, width: 120 }} />
              </div>
              {activeEntries.length > 0 && (
                <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between' }}>
                  <div><div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>Total Qty</div><div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{formTotalQty}</div></div>
                  <div style={{ textAlign: 'right' }}><div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>Total Amount</div><div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-amber)' }}>₹{formTotalCost.toLocaleString('en-IN')}</div></div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleBulkAdd} disabled={!articleType || activeEntries.length === 0} style={{
                  flex: 1, padding: 12,
                  background: (articleType && activeEntries.length > 0) ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'var(--bg-elevated)',
                  border: 'none', borderRadius: 'var(--radius-sm)', color: (articleType && activeEntries.length > 0) ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: (articleType && activeEntries.length > 0) ? 'pointer' : 'default',
                }}>Add {activeEntries.length} Size{activeEntries.length !== 1 ? 's' : ''} to Inventory</button>
                <button onClick={() => setShowAdd(false)} style={{ padding: '12px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CATEGORIES MODAL ═══ */}
      {showCategories && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setShowCategories(false); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 0, width: 460, maxWidth: '95vw', boxShadow: 'var(--shadow-elevated)', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', zIndex: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Manage Categories</div>
                <button onClick={() => setShowCategories(false)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
              </div>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>New Category</label>
                <input value={newCatName} onChange={e => setNewCatName(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }} placeholder="e.g. Oversized T-Shirt, Joggers" />
                <label style={{ ...labelStyle, fontSize: 10 }}>Custom Sizes (comma separated, optional)</label>
                <input value={newCatSizes} onChange={e => setNewCatSizes(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }} placeholder="e.g. 28, 30, 32 (leave blank for XS-XXL)" />
                <button onClick={handleAddCategory} disabled={!newCatName} style={{
                  padding: '10px 18px', width: '100%',
                  background: newCatName ? 'linear-gradient(135deg, var(--accent-purple), #7c3aed)' : 'var(--bg-elevated)',
                  border: 'none', borderRadius: 'var(--radius-sm)', color: newCatName ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: newCatName ? 'pointer' : 'default',
                }}><Plus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />Add Category</button>
              </div>
              <label style={labelStyle}>Your Categories ({categories.length})</label>
              {categories.length > 0 ? categories.map((c: any) => (
                <div key={c.id} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Sizes: {c.sizes?.length > 0 ? c.sizes.join(', ') : 'Default (XS-XXL)'}</div>
                  </div>
                  <button onClick={async () => { await inventory.deleteCategory(c.id); load(); }} style={{ background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', display: 'flex' }}><Trash2 size={12} color="var(--accent-red)" /></button>
                </div>
              )) : <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>No categories yet</div>}
            </div>
          </div>
        </div>
      )}

      {/* ═══ PURCHASE MODAL ═══ */}
      {showPurchase !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setShowPurchase(null); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32, width: 400, maxWidth: '95vw', boxShadow: 'var(--shadow-elevated)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 24 }}>Add Purchase / Restock</div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Quantity</label>
              <input type="number" value={purchaseForm.quantity} onChange={e => setPurchaseForm({ ...purchaseForm, quantity: Number(e.target.value) })} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Total Cost (₹)</label>
              <input type="number" value={purchaseForm.total_cost} onChange={e => setPurchaseForm({ ...purchaseForm, total_cost: Number(e.target.value) })} style={inputStyle} />
            </div>
            {purchaseForm.quantity > 0 && <div style={{ fontSize: 12, color: 'var(--accent-amber)', marginBottom: 14 }}>Cost per unit: ₹{(purchaseForm.total_cost / purchaseForm.quantity).toFixed(2)}</div>}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Notes</label>
              <input value={purchaseForm.notes} onChange={e => setPurchaseForm({ ...purchaseForm, notes: e.target.value })} style={inputStyle} placeholder="Supplier, batch..." />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => handlePurchase(showPurchase)} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg, var(--accent-green), #059669)', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Confirm</button>
              <button onClick={() => setShowPurchase(null)} style={{ flex: 1, padding: 12, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Inventory;
